import { defineComponent as _defineComponent } from 'vue'

import { Context } from "@nuxt/types";
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  ssrRef,
  useContext,
  useMeta,
  useRoute,
  useStore,
} from "@nuxtjs/composition-api";

import { useAmplitude, useTranslate } from "@/composables";
import { useCreatorShop } from "@/store/creator-shop";
import { getGiftGuidesStore } from "@/store/gift-guides";
import { getProfilesStore } from "@/store/profiles";
import { formatErrorMessage, handleErrorStatusCode, handleServiceError } from "@/utils/api";
import { NOT_FOUND } from "@/utils/enums/http-codes";
import logger, { logLevel } from "@/utils/logger";
import { convert, fromV1, toV3 } from "@/utils/transform/ordered-uuid";

const __default__ = defineComponent({
  name: "GiftGuides",
  async asyncData({ error, params, store, redirect }: Context) {
    // Try to convert from post gift guide sourced IDs to collection gift guide IDs
    try {
      const convertedId = convert(params.id, fromV1, toV3);
      if (convertedId) {
        return redirect({
          name: "explore-displayName-giftguides-id",
          params: { ...params, id: convertedId },
        });
      }
    } catch {
      // ID should be in v3 format already. Do nothing
    }

    try {
      const profilesStore = getProfilesStore(store);
      const giftGuideStore = getGiftGuidesStore(store);

      if (!giftGuideStore.hasFeedBeenFetched(params.id)) {
        await Promise.all([
          giftGuideStore.fetchGiftGuide({ id: params.id }),
          giftGuideStore.fetchGiftGuideProducts({ id: params.id }),
        ]);
      }
      if (giftGuideStore.giftGuides[params.id]?.subtype !== "GIFT_GUIDE") {
        return redirect({
          name: `explore-displayName-productsets-id`,
          params: { displayName: params.displayName, id: params.id },
        });
      }
      if (!giftGuideStore.haveCategoriesBeenFetched) {
        await giftGuideStore.fetchLocalizedCategories();
      }
      if (!profilesStore.hasProfileBeenFetchedByName(params.displayName)) {
        await profilesStore.fetchProfileByDisplayName(params.displayName);
      }
      if (giftGuideStore.feeds[params.displayName] === undefined) {
        await giftGuideStore.fetchGiftGuidesByProfile({
          displayName: params.displayName,
          profileId: profilesStore.getIdByDisplayName(params.displayName) || "",
        });
      }
    } catch (err) {
      logger(logLevel.ERROR, "Error fetching gift guides", {}, err);
      error({
        statusCode: NOT_FOUND,
        message: `Error fetching gift guides (${params.id}) - ${formatErrorMessage(err)}`,
      });
    }

    return params;
  },
  head: {},
});

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const paginate = ssrRef(false, "paginate");
const pending = ssrRef(false, "pending");
const error = ref<unknown | null>(null);
const ltkFetchState = computed(() => ({ error: error.value, pending: pending.value }));

const store = useStore();
const context = useContext();
const amplitude = useAmplitude();
const route = useRoute();
const { t } = useTranslate();

const localId = computed(() => route.value?.params?.id);
const displayName = computed(() => route.value?.params?.displayName);

const fetch = async () => {
  const giftGuideStore = getGiftGuidesStore(store);
  if (paginate.value === true || !giftGuideStore.hasFeedBeenFetched(localId.value)) {
    try {
      pending.value = true;
      await giftGuideStore.fetchGiftGuideProducts({ id: localId.value });
      pending.value = false;
    } catch (err) {
      error.value = err;
      pending.value = false;
      return handleServiceError({
        context,
        error: {
          status: handleErrorStatusCode(err),
          errorCode: "product-set-items-not-found",
          developerMessage: `Error fetching gift guide products (${localId.value})`,
        },
        originalError: err,
      });
    }
  }
  paginate.value = true;
};

onMounted(() => {
  setReferringTab();
  amplitude?.sendEvent("/page/product-set-details", giftGuideEventProps.value);
});

onBeforeUnmount(() => {
  useCreatorShop().completeShopTabJourney();
});

const setReferringTab = () => {
  useCreatorShop().setReferringShopTab();
};

const translatedAttributes = computed(() => {
  const giftGuideStore = getGiftGuidesStore(store);
  const attributes = giftGuideStore.giftGuides[localId.value]?.attributes;
  if (attributes) {
    return giftGuideStore.localizedAttributes(attributes);
  }
  return null;
});

const referringComponent = computed(() => {
  return amplitude?.getReferringComponentByPage(route.value?.name || "", "profile image");
});

const giftGuideEventProps = computed(() => {
  return {
    "gift-guide": true,
    "product-set-subtype": "GIFT_GUIDE",
    "is-gift-guide": true,
    "gift-guide-category": translatedAttributes.value?.recipient,
    "gift-guide-occasion": translatedAttributes.value?.occasion,
    "gift-guide-id": localId.value,
    "publisher-id": profileId.value,
    title: giftGuideName.value,
  };
});

const categoryText = computed(() => {
  return translatedAttributes.value?.recipient ?? "";
});

const giftGuideName = computed(() => {
  return getGiftGuidesStore(store).giftGuides[localId.value]?.title ?? "";
});

const giftGuideDescription = computed(() => {
  return getGiftGuidesStore(store).giftGuides[localId.value]?.description;
});

const shopProductIds = computed(() => {
  return getGiftGuidesStore(store).feeds[localId.value]?.ids ?? [];
});

const hasNextPage = computed(() => {
  return getGiftGuidesStore(store).getFeedHasMorePages(localId.value);
});

const profileId = computed(() => {
  const profilesStore = getProfilesStore(store);
  return profilesStore.getIdByDisplayName(displayName.value);
});

const profileAvatar = computed(() => {
  const profilesStore = getProfilesStore(store);
  return profilesStore.profiles[profileId.value ?? ""]?.avatarUrl;
});

const titleGiftGuideLength = 28;
const descriptionGiftGuideNameLength = 70;
// strip punctuation at the end of product set name
const giftGuideNm = giftGuideName.value?.replace(/\s*[.,/#!$%^&*;:{}=\-_`~()]\s*$/, "");

const pageTitle = t("{displayName}'s {giftGuideName} Gift Guide on LTK", {
  displayName: displayName.value,
  giftGuideName: context.$metaService.truncateString(giftGuideName.value, titleGiftGuideLength),
}); // Page title for the creator gift guide page
const pageDescription = t("Check out this specially curated {giftGuideName} gift guide from {displayName}", {
  displayName: displayName.value,
  giftGuideName: context.$metaService.truncateString(giftGuideNm, descriptionGiftGuideNameLength),
}); // Description for creator gift guide page
const paidLinks = t("Paid links");

useMeta(() => {
  const head = context.$metaService.setPageMeta({
    title: pageTitle,
    customTitle: true,
    description: pageDescription,
  });
  if (profileAvatar.value) {
    const height = 400;
    const width = 400;
    const image = `${profileAvatar.value}?fit=crop&w=${width}&h=${height}`;

    head.meta?.push(...context.$metaService.setOGImg(image, width));
  }
  return head;
});

return { __sfc: true,paginate, pending, error, ltkFetchState, store, context, amplitude, route, t, localId, displayName, fetch, setReferringTab, translatedAttributes, referringComponent, giftGuideEventProps, categoryText, giftGuideName, giftGuideDescription, shopProductIds, hasNextPage, profileId, profileAvatar, titleGiftGuideLength, descriptionGiftGuideNameLength, giftGuideNm, pageTitle, pageDescription, paidLinks }
}

})